/**
 * Intl.NumberFormat has a `trailingZeroDisplay` option that allows you to control how trailing
 * zeros are displayed. It only reached cross-browser support in late 2023, so we need to provide
 * a fallback.
 */
export const filterZeroFraction = parts => {
  const hasZeroFraction = parts.find(part => part.type === 'fraction' && (part.value === '00' || part.value === '000'));
  if (hasZeroFraction) {
    return parts.filter(({
      type
    }) => type !== 'fraction' && type !== 'decimal');
  }
  return parts;
};
/**
 * Format an integer/string into a localized currency string (uses Intl.NumberFormat).
 *
 * Reference for expected currency format:
 * https://help.salesforce.com/s/articleView?id=sf.admin_supported_currencies.htm&type=5
 *
 * Read more about Int.NumberFormat on MDN
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat
 *
 * To omit the currency symbol, use:
 * formatCurrency({
 *   currencyCode: 'USD',
 *   amount: 1000,
 *   locale: 'en-US',
 *   options: { currencyDisplay: 'code' }
 * ).replace('USD', '').trim()
 *
 * To omit the decimals, only if they are zero, use:
 * formatCurrency({
 *   currencyCode: 'USD',
 *   amount: 1000,
 *   locale: 'en-US',
 *   options: { trailingZeroDisplay: 'stripIfInteger' }
 * )
 */
export const formatCurrency = ({
  currencyCode,
  amount,
  locale,
  options = {}
}) => {
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    currencyDisplay: 'symbol',
    ...options
  });
  const resolvedOptions = formatter.resolvedOptions();

  // trailingZeroDisplay only reached cross-browser support in late 2023
  if (options?.trailingZeroDisplay === 'stripIfInteger' && !resolvedOptions.trailingZeroDisplay) {
    const parts = filterZeroFraction(formatter.formatToParts(Number(amount)));
    return parts.map(part => part.value).join('');
  }
  return formatter.format(Number(amount));
};
export const getCurrencyParts = ({
  currencyCode,
  amount,
  locale,
  options = {}
}) => {
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    currencyDisplay: 'symbol',
    ...options
  });
  const parts = formatter.formatToParts(Number(amount));
  const resolvedOptions = formatter.resolvedOptions();

  // trailingZeroDisplay only reached cross-browser support in late 2023
  if (options?.trailingZeroDisplay === 'stripIfInteger' && !resolvedOptions.trailingZeroDisplay) {
    return filterZeroFraction(parts);
  }
  return parts;
};